/**
 * BackshopDuplikatDialog.jsx — AP3
 *
 * Zeigt einen Vorausfüll-Dialog wenn beim Anlegen eines Backshop-Produkts
 * ein bestehender Master-Katalog-Eintrag gefunden wird.
 *
 * Props:
 *  match        – { id, product_name, ean, article_number, has_image, has_pdf, image_url }
 *  onAccept     – () => void  — Nutzer übernimmt das Produkt aus dem Master
 *  onEnrichImage – (file) => void — optionaler Bild-Upload
 *  onEnrichPdf   – (file) => void — optionaler PDF-Upload
 *  onCancel     – () => void
 *  loading      – bool
 */
import React, { useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { CheckCircle2, XCircle, Upload, Image as ImgIcon, FileText, Loader2 } from 'lucide-react';

function StatusIcon({ ok }) {
  return ok
    ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
    : <XCircle     className="h-4 w-4 text-amber-500 shrink-0" />;
}

export function BackshopDuplikatDialog({ match, onAccept, onEnrichImage, onEnrichPdf, onCancel, loading }) {
  const imgRef = useRef();
  const pdfRef = useRef();

  if (!match) return null;

  return (
    <Dialog open={!!match} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="duplikat-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImgIcon className="h-5 w-5 text-primary" />
            Produkt bereits im Katalog
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <p className="text-sm text-muted-foreground">
            Dieses Produkt ist bereits im gemeinsamen Katalog hinterlegt.
            Du kannst es direkt in dein Sortiment übernehmen — fehlende Bilder oder PDFs
            können ergänzt werden und stehen danach allen Standorten zur Verfügung.
          </p>

          {/* Produkt-Info */}
          <div className="bg-muted/40 rounded-xl p-4 space-y-2">
            <p className="text-sm font-semibold">{match.product_name}</p>
            {match.article_number && (
              <p className="text-xs text-muted-foreground">Art.-Nr.: {match.article_number}</p>
            )}
            {match.ean && (
              <p className="text-xs text-muted-foreground">EAN: {match.ean}</p>
            )}
            <div className="flex gap-4 pt-1">
              <div className="flex items-center gap-1.5 text-xs">
                <StatusIcon ok={match.has_image} />
                <span>Bild</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <StatusIcon ok={match.has_pdf} />
                <span>PDF / Rezept</span>
              </div>
            </div>
          </div>

          {/* Optionale Anreicherung */}
          {(!match.has_image || !match.has_pdf) && (
            <div className="border border-dashed border-border rounded-xl p-3 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Fehlende Inhalte ergänzen (optional)
              </p>
              <p className="text-xs text-muted-foreground">
                Hochgeladene Bilder und PDFs werden automatisch für alle anderen Standorte freigegeben — anonym, ohne Kundenbezug.
              </p>
              <div className="flex gap-2 flex-wrap">
                {!match.has_image && (
                  <>
                    <Button
                      size="sm" variant="outline" className="gap-1.5 text-xs"
                      onClick={() => imgRef.current?.click()}
                      data-testid="enrich-image-btn"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      Bild hochladen
                    </Button>
                    <input
                      ref={imgRef} type="file" accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={e => e.target.files?.[0] && onEnrichImage(e.target.files[0])}
                    />
                  </>
                )}
                {!match.has_pdf && (
                  <>
                    <Button
                      size="sm" variant="outline" className="gap-1.5 text-xs"
                      onClick={() => pdfRef.current?.click()}
                      data-testid="enrich-pdf-btn"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      PDF / Rezept hochladen
                    </Button>
                    <input
                      ref={pdfRef} type="file" accept="application/pdf"
                      className="hidden"
                      onChange={e => e.target.files?.[0] && onEnrichPdf(e.target.files[0])}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="duplikat-cancel-btn">
            Abbrechen
          </Button>
          <Button onClick={onAccept} disabled={loading} data-testid="duplikat-accept-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            In mein Sortiment übernehmen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
